import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Icon from "../../components/AppIcon";
import { useAuth } from "../../context/AuthContext";
import {
  connectCurrentAgrochainLink,
  provisionAgrochainAccount,
  resolveAgrochainLink,
} from "../../services/agrochainAuthApi";
import { getPlatformNameFromUrl } from "../../utils/platformIdentity";

const LinkAgrochainAccountPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const {
    isAuthenticated,
    user,
    setSessionFromApi,
    getDefaultRedirectByRole,
  } = useAuth();
  const [status, setStatus] = useState("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [previewAccount, setPreviewAccount] = useState(null);
  const [isProvisioning, setIsProvisioning] = useState(false);
  const lastAttemptRef = useRef("");

  const linkToken = searchParams.get("token") || "";
  const agrochainOrigin = searchParams.get("agrochainOrigin") || "";
  const marketOrigin = window.location.origin;
  const linkedPlatformName = useMemo(
    () => getPlatformNameFromUrl(agrochainOrigin, "Agrochain"),
    [agrochainOrigin],
  );
  const requestBody = useMemo(
    () => ({
      linkToken,
      agrochainOrigin,
      marketOrigin,
    }),
    [agrochainOrigin, linkToken, marketOrigin],
  );

  useEffect(() => {
    if (!linkToken || !agrochainOrigin) {
      setStatus("error");
      setErrorMessage("Bağlantı bilgisi eksik veya hatalı.");
      return;
    }

    const attemptKey = `${isAuthenticated ? "auth" : "guest"}:${linkToken}:${agrochainOrigin}`;
    if (lastAttemptRef.current === attemptKey) {
      return;
    }
    lastAttemptRef.current = attemptKey;

    const run = async () => {
      setStatus("loading");
      setErrorMessage("");

      try {
        if (isAuthenticated) {
          const response = await connectCurrentAgrochainLink(requestBody);
          setSessionFromApi(response);
          navigate(getDefaultRedirectByRole(response?.user?.role), { replace: true });
          return;
        }

        const response = await resolveAgrochainLink(requestBody);
        if (response?.mode === "authenticated" && response?.auth) {
          setSessionFromApi(response.auth);
          navigate(getDefaultRedirectByRole(response?.auth?.user?.role), { replace: true });
          return;
        }

        setPreviewAccount(response?.agrochainAccount || null);
        setStatus("choice");
      } catch (error) {
        setStatus("error");
        setErrorMessage(error?.message || "Agrochain bağlantısı doğrulanamadı.");
      }
    };

    run();
  }, [
    agrochainOrigin,
    getDefaultRedirectByRole,
    isAuthenticated,
    linkToken,
    navigate,
    requestBody,
    setSessionFromApi,
  ]);

  const handleContinueWithAgrochain = async () => {
    setIsProvisioning(true);
    setErrorMessage("");

    try {
      const response = await provisionAgrochainAccount(requestBody);
      setSessionFromApi(response);
      navigate(getDefaultRedirectByRole(response?.user?.role), { replace: true });
    } catch (error) {
      setErrorMessage(error?.message || "Üyelik Agrochain bilgileriyle tamamlanamadı.");
    } finally {
      setIsProvisioning(false);
    }
  };

  const handleGoToLogin = () => {
    navigate("/login", {
      state: {
        from: `${location.pathname}${location.search}`,
      },
    });
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-2xl rounded-2xl border border-border bg-surface shadow-elevation-3">
        <div className="border-b border-border px-8 py-6">
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-primary">
              <Icon name="Link2" size={24} />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-text-primary">Agrochain hesabını bağla</h1>
              <p className="mt-2 text-sm leading-6 text-text-secondary">
                {isAuthenticated
                  ? "Açık Ürün Borsası oturumunuz bu Agrochain hesabıyla eşleştiriliyor."
                  : "Agrochain hesabınızı Ürün Borsası hesabınızla eşleştirmek için devam edin."}
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-6 px-8 py-6">
          {status === "loading" ? (
            <div className="rounded-xl border border-border bg-background px-5 py-5 text-sm text-text-secondary">
              {isAuthenticated
                ? "Açık Ürün Borsası hesabınız bağlanıyor..."
                : `${linkedPlatformName} hesabı doğrulanıyor...`}
            </div>
          ) : null}

          {status === "choice" ? (
            <>
              <div className="rounded-xl border border-border bg-background px-5 py-5">
                <div className="grid gap-5 md:grid-cols-2">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-[0.18em] text-text-secondary">
                      Bağlanacak Platform
                    </p>
                    <p className="mt-2 text-sm font-semibold text-text-primary">{linkedPlatformName}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-[0.18em] text-text-secondary">
                      Agrochain Hesabı
                    </p>
                    <p className="mt-2 text-sm font-semibold text-text-primary">
                      {previewAccount?.email || "Bulunamadı"}
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-border bg-background px-5 py-5">
                <h2 className="text-lg font-semibold text-text-primary">Agromart hesabınız var mı?</h2>
                <p className="mt-2 text-sm leading-6 text-text-secondary">
                  Hesabınız varsa oturum açın, bu Agrochain hesabını mevcut Ürün Borsası hesabınıza bağlayalım.
                </p>
                <button
                  type="button"
                  onClick={handleGoToLogin}
                  className="mt-4 inline-flex items-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 transition-colors"
                >
                  Agromart hesabımla giriş yap
                </button>
              </div>

              <div className="rounded-xl border border-border bg-background px-5 py-5">
                <h2 className="text-lg font-semibold text-text-primary">Hesabınız yoksa Agrochain ile devam edin</h2>
                <p className="mt-2 text-sm leading-6 text-text-secondary">
                  Devam ederseniz yeni Ürün Borsası hesabınız Agrochain bilgilerinizle otomatik oluşturulur ve
                  rolünüz <span className="font-medium text-text-primary">Market User</span> olarak açılır.
                </p>
                <button
                  type="button"
                  onClick={handleContinueWithAgrochain}
                  disabled={isProvisioning}
                  className="mt-4 inline-flex items-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-70 transition-colors"
                >
                  {isProvisioning ? "Üyelik tamamlanıyor..." : "Agrochain hesabıyla üyeliği tamamla"}
                </button>
              </div>
            </>
          ) : null}

          {status === "error" || errorMessage ? (
            <div className="rounded-xl border border-error/20 bg-error-100 px-5 py-4 text-sm text-error">
              {errorMessage || "Bağlantı akışı tamamlanamadı."}
            </div>
          ) : null}

          {isAuthenticated && user ? (
            <div className="rounded-xl border border-success/20 bg-success-100 px-5 py-4 text-sm text-success">
              Açık oturum: {user.email}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default LinkAgrochainAccountPage;
